<script>
  // ── Lager-Insel: Shell ────────────────────────────────────────────────────
  // Die Datenschicht bleibt framework-neutral in lager-journal.js /
  // lager-data.js und wird über window.* gelesen (Muster: Dashboard-Insel).
  //
  // Sprache über window.t() wie in den Dashboard-Modulen — die i18n-Runtime
  // lebt in utils.js im Hauptbundle, die Insel ruft sie nur auf.
  //
  // Seit L6 ist der Platzhalter weg: Die Shell hält den Token-Satz, den
  // Zustand „Maske offen" und sonst nichts. Was zu sehen ist, entscheidet
  // views/Bestand.svelte.
  import { onMount } from 'svelte';
  import Bestand from './views/Bestand.svelte';
  import ZutatDetail from './views/ZutatDetail.svelte';
  import EingangBuchen from './views/EingangBuchen.svelte';

  let maskeOffen = $state(false);
  // Die Zutat, mit der die Eingangsmaske aufgeht — `null` heißt leer, wie vom
  // CTA der Liste. Sie liegt HIER und nicht im Detail, weil die Maske ein
  // Geschwister der Ansichten ist und nicht ihr Kind (s. Token-Kommentar unten).
  let maskeVorwahl = $state(/** @type {string|null} */ (null));

  // ── Navigation als Zustand der Insel, nicht als Route (Plan 17.6) ────────
  // `null` heißt Liste. Kein Verlauf-Eintrag, kein Deep-Link: Das Modul liegt
  // hinter einer Beta-Wall, und ein teilbarer Link auf eine Zutat wäre ein
  // Versprechen, das niemand eingelöst hat. Der Preis ist, dass der
  // Zurück-Knopf des Browsers das Lager verlässt statt zur Liste zu gehen —
  // vertretbar, solange es EINE Ebene ist.
  let offeneZutat = $state(/** @type {string|null} */ (null));

  // ── Sprachwechsel ────────────────────────────────────────────────────────
  // `window.t()` ist für Svelte nicht reaktiv: Jeder Aufruf im Markup und jede
  // Konstante wie die Achsenmarken wird EINMAL beim Mounten ausgewertet. Ohne
  // diesen Schlüssel blieb nach dem Umschalten die ganze Bestandsliste in der
  // alten Sprache stehen, während die App drumherum schon übersetzt war.
  // Dasselbe Muster wie `{#key lang}` in Dashboard.svelte: Die Ansichten werden
  // neu montiert, der Zustand DIESER Shell (offene Zutat, Maske) bleibt.
  let lang = $state(window.currentLang?.() ?? 'de');

  // Das Journal wird erst beim Öffnen des Lagers geladen — ein Konto ohne
  // Freischaltung soll dafür bei jedem Start keinen Lesevorgang bezahlen.
  onMount(() => {
    window.lagerJournalLaden?.();
    const beiSprache = () => { lang = window.currentLang?.() ?? lang; };
    // Von außen geöffnet (window.lagerZutatOeffnen in main.js) — die
    // Zutatentabelle eines Backtags zeigt „Im Lager ansehen".
    const beiZutat = (/** @type {CustomEvent} */ e) => {
      const id = e?.detail?.zutatId;
      if (id) { maskeOffen = false; offeneZutat = id; }
    };
    document.addEventListener('i18n:changed', beiSprache);
    document.addEventListener('lager:zutat-oeffnen', beiZutat);
    return () => {
      document.removeEventListener('i18n:changed', beiSprache);
      document.removeEventListener('lager:zutat-oeffnen', beiZutat);
    };
  });
</script>

{#key lang}
  {#if offeneZutat}
    <ZutatDetail
      zutatId={offeneZutat}
      onzurueck={() => (offeneZutat = null)}
      oneingang={(id) => { maskeVorwahl = id; maskeOffen = true; }}
    />
  {:else}
    <Bestand
      onbuchen={() => { maskeVorwahl = null; maskeOffen = true; }}
      onoeffnen={(id) => (offeneZutat = id)}
    />
  {/if}

  {#if maskeOffen}
    <EingangBuchen
      vorwahl={maskeVorwahl}
      onschliessen={() => { maskeOffen = false; maskeVorwahl = null; }}
    />
  {/if}
{/key}

<style>
  /* Eigener Token-Namensraum, gescopt auf das PANEL — Hausmuster wie
     #panel-rezepte { --rui-* } in css/style.css. Die Werte stammen aus dem
     Design System (tokens/colors.css).

     Auf #panel-lager und nicht auf einer eigenen Hülle: Die Eingangsmaske ist
     ein GESCHWISTER der Bestandsansicht, kein Kind. Lägen die Tokens auf der
     Ansicht, wäre im Dialog jedes var(--lg-card) undefiniert — und eine
     ungültige Hintergrundfarbe heißt transparent, nicht Weiß. Genau das war
     der Fall. :global, weil Svelte sonst eine Komponentenklasse an den
     Selektor hängt und er das Panel nicht mehr trifft.

     `.lg-tokens` (L10): Die Abschluss-Maske liegt unter `body`, nicht im
     Panel — beim Abschluss ist es verborgen. Sie trägt die Klasse und bekommt
     DENSELBEN Satz; eine Kopie wäre beim ersten Nachjustieren
     auseinandergelaufen. Voraussetzung: Diese Komponente ist montiert, denn
     `css: 'injected'` hängt ihre Styles erst beim Mount ein. main.js mountet
     sie bei `app:ready`, also vor jedem möglichen Abschluss. */
  :global(#panel-lager), :global(.lg-tokens) {
    --lg-ink: #292524;
    --lg-ink-soft: #57534e;
    /* --text-muted (stone-500). Der Namensraum sprang bisher von 600 auf 400
       und traf damit die Kit-Stufe dazwischen nicht — die Filterchips standen
       deshalb eine Stufe zu dunkel. */
    --lg-ink-mittel: #78716c;
    --lg-ink-mute: #a8a29e;
    --lg-line: #e7e5e4;
    --lg-card: #ffffff;
    --lg-tint: #faf8f5;
    --lg-accent: #c05a3e;
    --lg-accent-hover: #a34935;
    --lg-focus: 0 0 0 3px rgba(41, 37, 36, 0.14);

    /* Getönte Glasflächen: dieselbe Rezeptur wie --nav-bg-light im System,
       damit Filterleiste und Fußleiste zur Topbar der App passen. */
    --lg-app-glas: rgba(245, 243, 240, 0.88);

    /* Das Raster der breiten Bestandszeile — Name | Skala | Menge. Es steht
       hier, weil ZWEI Bauteile es treffen muessen: die Zeile selbst und die
       Achse ueber der Karte (L7). Zwei handgeschriebene Spaltensaetze waeren
       beim ersten Nachjustieren um ein paar Pixel versetzt, und genau das
       faellt an einer Achse auf. */
    --lg-zeilen-raster: 210px minmax(0, 1fr) 92px;
    --lg-zeilen-abstand: 24px;

    /* ── Skala der Reichweite (L6) ──────────────────────────────────────
       Graustufen aus tokens/colors.css: --stone-100/150/400/700. */
    --lg-line-fein: #f5f5f4;  /* Zeilentrenner in der Liste */
    --lg-track: #f5f5f4;      /* Spur der Skala */
    --lg-tick: #f0ede9;       /* Teilung je Backtag, zugleich Fortschritt aus */
    --lg-fill: #c4bfba;       /* Balken */
    --lg-fill-stark: #78716c; /* Balken bei Hover auf der Zeile */
    --lg-plan: #44403c;       /* Marke des naechsten geplanten Backtags */
    --lg-prog-on: #a8a29e;    /* erfasster Backtag im Fortschritt */

    /* Eingang in der Bewegungsliste — --success-* aus dem System. */
    --lg-ein-bg: #dcfce7;
    --lg-ein-ink: #15803d;

    /* ── Fehlmenge ──────────────────────────────────────────────────────
       Diese drei sind die einzige Farbe im Modul, die eine AUSSAGE traegt,
       und sie kommen deshalb aus dem System statt aus dem Kit: seit dem
       offenen Punkt O-2 liegen sie als --attention-* in tokens/colors.css,
       abgegrenzt gegen --warning-* (Zeitkonflikte, kosten nichts) und die
       unantastbare Ressourcenfarbe Ofen. Hue ~345 statt ~40 und ~21.
       Vorgezogen von L5 auf L6: Reichweite.svelte bringt den Zustand
       `fehlt` von Anfang an mit und braucht seine Farbe, sobald es ihn
       gibt — nicht erst, sobald er vorkommt (Plan 14.9). */
    --lg-fehlt-bg: #fff1f2;
    --lg-fehlt-edge: #fb7185;
    --lg-fehlt-ink: #9f1239;
    --lg-fehlt-spur: rgba(159, 18, 57, 0.10);
    --lg-fehlt-tick: rgba(159, 18, 57, 0.08);

    /* ── Hinweis (L10, Abschluss-Maske) ─────────────────────────────────
       Amber statt Rose: „Prüfen" ist eine Bitte, hinzusehen, keine Gefahr.
       Rot für jede Zeile ohne Zuordnung las sich im Betrieb als Alarm
       (2026-09-15). Die Werte sind die von `.notice--warn` in style.css —
       dieselbe Farbe, die die App für einen Hinweis schon spricht. Der
       Strich ist eine Stufe kräftiger: 3 px in #fde68a verschwinden auf Weiß. */
    --lg-hinweis-bg: #fef3c7;
    --lg-hinweis-edge: #fde68a;
    --lg-hinweis-strich: #f59e0b;
    --lg-hinweis-ink: #b45309;
  }

  /* Das Panel trägt den Grund der App, nicht Weiß: Die Liste besteht aus
     Karten, und die brauchen etwas, worauf sie liegen können. */
  :global(#panel-lager) { background: #f0ede9; min-height: 60vh; }
</style>
